import { useEffect, useState } from 'react';
import { StandardCancelFlow } from '~/constants/ff.posthog';
import { isStandardCancelArm } from '~/services/posthog';
import type { TFeatureFlagVariantStatus } from './useFeatureFlagVariantState';
import { usePosthog } from './usePosthog';

type TPosthogService = ReturnType<typeof usePosthog>;

const readIsTreatment = (posthogService: TPosthogService | null): boolean =>
  isStandardCancelArm(posthogService?.peekFeatureFlagVariant(StandardCancelFlow) ?? null);

const readStatus = (posthogService: TPosthogService | null): TFeatureFlagVariantStatus => {
  // Without PostHog no flags will ever arrive, so the office stays on today's flow.
  if (!posthogService) return 'disabled';
  if (!posthogService.areFlagsReady()) return 'loading';
  return readIsTreatment(posthogService) ? 'enabled' : 'disabled';
};

// Peeks only, so rendering never records an exposure; re-reads whenever flags (re)load.
const useStandardCancelFlowRead = <T,>(read: (posthogService: TPosthogService | null) => T): T => {
  const posthogService = usePosthog();

  const [value, setValue] = useState<T>(() => read(posthogService));

  useEffect(() => {
    if (!posthogService) return undefined;

    let active = true;
    const sync = () => {
      if (active) setValue(read(posthogService));
    };

    sync();
    const unsubscribe = posthogService.onFeatureFlags(sync);

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [posthogService, read]);

  return value;
};

/**
 * Render-time read of the standard cancel flow A/B (GOTU-15983): true only for `treatment`. Before
 * PostHog answers it uses the browser's cached flags, so it can't tell loading from control; use
 * useStandardCancelFlowState() for that. Call getStandardCancelFlow() when the office starts a cancel.
 */
export const useStandardCancelFlow = (): boolean => useStandardCancelFlowRead(readIsTreatment);

/** `loading` until PostHog answers, then `enabled` for `treatment` and `disabled` otherwise. */
export const useStandardCancelFlowState = (): TFeatureFlagVariantStatus =>
  useStandardCancelFlowRead(readStatus);
